const STUDIO_ANALYTICS_CONFIG_SELECTOR = 'script[data-cove-studio-analytics-config]';
const STUDIO_ANALYTICS_RUNTIME_SELECTOR = 'script[data-cove-studio-analytics-runtime]';
export type StudioAnalyticsSettings = {
  customJs: string;
};

export type StudioAnalyticsRuntimeContext = {
  currentProjectId?: string | null;
  currentProjectTypeId?: string | null;
  currentSiteId?: string | null;
  currentUserProjectId?: string | null;
};

const trimValue = (value: unknown) => String(value || '').trim();

const serializeInlineJson = (value: unknown) =>
  JSON.stringify(value)
    .replace(/</g, '\\u003C')
    .replace(/-->/g, '--\\u003E')
    .replace(/\u2028/g, '\\u2028')
    .replace(/\u2029/g, '\\u2029');

export const normalizeStudioAnalyticsSettings = (
  settings?: Partial<StudioAnalyticsSettings> | null,
): StudioAnalyticsSettings => ({
  customJs: typeof settings?.customJs === 'string' ? settings.customJs : '',
});

export const areStudioAnalyticsSettingsEqual = (
  left: StudioAnalyticsSettings,
  right: StudioAnalyticsSettings,
) => left.customJs === right.customJs;

const getProjectTrackingContext = (context: StudioAnalyticsRuntimeContext) => ({
  currentUserProjectId: trimValue(context.currentUserProjectId) || undefined,
  currentProjectId: trimValue(context.currentProjectId) || undefined,
  currentProjectTypeId: trimValue(context.currentProjectTypeId) || undefined,
  currentSiteId: trimValue(context.currentSiteId) || undefined,
});

const buildStudioAnalyticsRuntimeScript = (context: StudioAnalyticsRuntimeContext) => {
  const projectTrackingContext = serializeInlineJson(getProjectTrackingContext(context));

  return `(function () {
  try {
    var configNode = document.querySelector(${serializeInlineJson(STUDIO_ANALYTICS_CONFIG_SELECTOR)});
    if (!configNode) return;

    var rawConfig = {};
    try {
      rawConfig = JSON.parse(configNode.textContent || '{}');
    } catch (error) {
      console.warn('[StudioAnalytics] Failed to parse analytics config.', error);
      rawConfig = {};
    }

    var customJs = rawConfig && typeof rawConfig.customJs === 'string' ? rawConfig.customJs : '';
    var projectContext = ${projectTrackingContext};
    if (customJs.trim()) {
      try {
        var customRunner = new Function('window', 'document', 'projectContext', customJs);
        customRunner(window, document, projectContext);
      } catch (error) {
        console.warn('[StudioAnalytics] Custom JavaScript failed.', error);
      }
    }
  } catch (error) {
    console.warn('[StudioAnalytics] Failed to initialize tracking runtime.', error);
  }
})();`;
};

const removeStudioAnalyticsScripts = (doc: Document) => {
  doc.querySelectorAll(STUDIO_ANALYTICS_CONFIG_SELECTOR).forEach((node) => node.remove());
  doc.querySelectorAll(STUDIO_ANALYTICS_RUNTIME_SELECTOR).forEach((node) => node.remove());
};

export const removeStudioAnalyticsTrackingScriptsFromDocument = (
  doc: Document | null | undefined,
) => {
  if (!doc) return;
  removeStudioAnalyticsScripts(doc);
};

export const stripStudioAnalyticsTrackingScript = (htmlContent: string) => {
  if (!trimValue(htmlContent)) return htmlContent;

  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlContent, 'text/html');
  removeStudioAnalyticsScripts(doc);
  return '<!DOCTYPE html>\n' + doc.documentElement.outerHTML;
};

const ensureDocumentHead = (doc: Document) => {
  const htmlElement = doc.documentElement;
  if (!htmlElement) return null;

  let head = doc.head;
  if (!head) {
    head = doc.createElement('head');
    if (doc.body) {
      htmlElement.insertBefore(head, doc.body);
    } else {
      htmlElement.appendChild(head);
    }
  }

  return head;
};

export const parseStudioAnalyticsSettingsFromHtml = (htmlContent: string) => {
  if (!trimValue(htmlContent)) return null;

  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlContent, 'text/html');
  const configNode = doc.querySelector(STUDIO_ANALYTICS_CONFIG_SELECTOR);
  if (!configNode?.textContent) return null;

  try {
    return normalizeStudioAnalyticsSettings(JSON.parse(configNode.textContent));
  } catch {
    return null;
  }
};

export const upsertStudioAnalyticsInHtml = (
  htmlContent: string,
  settings: StudioAnalyticsSettings,
  runtimeContext: StudioAnalyticsRuntimeContext = {},
) => {
  if (!trimValue(htmlContent)) return htmlContent;

  const normalizedSettings = normalizeStudioAnalyticsSettings(settings);
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlContent, 'text/html');
  const head = ensureDocumentHead(doc);
  if (!head) return htmlContent;

  removeStudioAnalyticsScripts(doc);

  if (!normalizedSettings.customJs.trim()) {
    return '<!DOCTYPE html>\n' + doc.documentElement.outerHTML;
  }

  const configNode = doc.createElement('script');
  configNode.setAttribute('type', 'application/json');
  configNode.setAttribute('data-cove-studio-analytics-config', 'true');
  configNode.textContent = serializeInlineJson(normalizedSettings);
  head.appendChild(configNode);

  const runtimeNode = doc.createElement('script');
  runtimeNode.setAttribute('data-cove-studio-analytics-runtime', 'true');
  runtimeNode.textContent = buildStudioAnalyticsRuntimeScript(runtimeContext);
  head.appendChild(runtimeNode);

  return '<!DOCTYPE html>\n' + doc.documentElement.outerHTML;
};
